import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  MousePointer,
  Package,
  Target,
  ClipboardList,
  TestTube,
  FlaskConical,
  MessageCircle,
  Settings,
  Car,
  Wand2,
} from 'lucide-react';
import SimulationCard from '../../components/SimulationCard';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import { LockedFeature } from '../../components/subscription/UpgradePrompt';
import { SURVEY_CHAT_ENTRY_IDS } from '../../config/segments';
import { resolveClientKey, allowedUseCasesFor } from '../../config/clients';

export type UseCaseType =
  | 'survey-and-focus-groups'
  | 'concept-test'
  | 'driving-concept-test'
  | 'vitamin-concept-test'
  | 'packaging-review-v2'
  | 'packaging-variant-comparison'
  | 'claims-testing'
  | 'attribution'
  | 'channel-strategy'
  | 'image-survey'
  | 'healthcare-chat-v2'
  | 'bluepill-chat'
  | 'hbs-chat'
  | 'test-use-case'
  | 'survey-creator'
  | 'survey-chat-154'
  | null;

interface UseCaseOption {
  label: string;
  type: UseCaseType;
  icon: React.ReactNode;
  description: string;
  redirect?: boolean;
}

interface CategoryConfig {
  title: string;
  useCases: UseCaseOption[];
}

interface LandingScreenProps {
  onSelectUseCase: (useCaseType: UseCaseType, useCaseTitle: string) => void;
}

interface GetCategoriesOptions {
  isAdmin: boolean;
  isPanasonicUser: boolean;
  isSmartypantsUser: boolean;
  isDemoUser: boolean;
  isKnfUser: boolean;
  isBacchusUser: boolean;
  isHbsUser: boolean;
  isWellnesspetUser: boolean;
  isMondelezUser: boolean;
  isArcherjerkyUser: boolean;
  isAmysUser: boolean;
  isGaiaUser: boolean;
}

const getCategories = ({ isAdmin, isPanasonicUser, isSmartypantsUser, isDemoUser, isKnfUser, isBacchusUser, isHbsUser, isWellnesspetUser, isMondelezUser, isArcherjerkyUser, isAmysUser, isGaiaUser }: GetCategoriesOptions): CategoryConfig[] => {
  // Determine which concept test to show (if any)
  // Panasonic users -> driving-concept-test as "Concept Test"
  // Admins -> all three (AI, driving, vitamin)
  // Demo, smartypants, bacchus, wellnesspet, mondelez, knf, archerjerky -> vitamin-concept-test as "Concept Test"
  const showVitaminConceptTest = !isPanasonicUser && (isAdmin || isDemoUser || isSmartypantsUser || isBacchusUser || isWellnesspetUser || isMondelezUser || isKnfUser || isArcherjerkyUser || isAmysUser || isGaiaUser);
  const showDrivingConceptTest = !isKnfUser && (isPanasonicUser || isAdmin);
  const showAiConceptTest = !isKnfUser && isAdmin;

  return [
    {
      title: 'Research',
      useCases: [
        {
          label: 'Surveys (Qual and Quant)',
          type: 'survey-and-focus-groups',
          icon: <ClipboardList className="w-5 h-5" />,
          description: 'Run qualitative and quantitative interviews to get deep insights',
        },
        {
          label: 'Focus Group',
          type: 'healthcare-chat-v2',
          icon: <MessageCircle className="w-5 h-5" />,
          description: 'Chat with an AI consumer-twin focus group',
          redirect: true,
        },
        // HBS Chat - only visible to HBS users (opens in new tab)
        ...(isHbsUser ? [{
          label: 'Focus Group',
          type: 'hbs-chat' as UseCaseType,
          icon: <MessageCircle className="w-5 h-5" />,
          description: 'Chat with an AI consumer-twin focus group',
          redirect: true,
        }] : []),
        // Survey Chat - visible to KNF, ArcherJerky, Amy's, and admin users
        ...((isKnfUser || isArcherjerkyUser || isAmysUser || isAdmin) ? [{
          label: 'Human Survey Insights',
          type: 'survey-chat-154' as UseCaseType,
          icon: <ClipboardList className="w-5 h-5" />,
          description: 'Chat with your human survey data and instantly surface key insights and themes.',
          redirect: true,
        }] : []),
        // Hidden for now — access via /chat-with-bluepill directly
        // {
        //   label: 'Chat with Bluepill',
        //   type: 'bluepill-chat',
        //   icon: <MessageCircle className="w-5 h-5" />,
        //   description: 'Chat with AI Consumer Twins powered by Bluepill',
        //   redirect: true,
        // },
        ...(isAdmin ? [{
          label: 'Survey Creation Agent',
          type: 'survey-creator' as UseCaseType,
          icon: <Wand2 className="w-5 h-5" />,
          description: 'Co-create a survey with an AI agent — draft, refine, and save in one flow',
          redirect: true,
        }] : []),
        ...(isAdmin ? [{
          label: 'Test Use Case',
          type: 'test-use-case' as UseCaseType,
          icon: <Settings className="w-5 h-5" />,
          description: 'Simulate a test to get insights',
        }] : []),
      ],
    },
    {
      title: 'Product & Innovation',
      useCases: [
        // Vitamin Concept Test - shown as "Concept Test" for admins, demo, smartypants
        ...(showVitaminConceptTest ? [{
          label: 'Concept Test',
          type: 'vitamin-concept-test' as UseCaseType,
          icon: <TestTube className="w-5 h-5" />,
          description: 'Test concepts with AI-simulated populations',
        }] : []),
        // Driving Concept Test - shown as "Concept Test" for Panasonic users only
        ...(showDrivingConceptTest ? [{
          label: 'Concept Test',
          type: 'driving-concept-test' as UseCaseType,
          icon: <Car className="w-5 h-5" />,
          description: 'Test concepts with AI-simulated populations',
        }] : []),
        // AI Concept Test - visible to admins (but not knf user)
        ...(showAiConceptTest ? [{
          label: 'AI Concept Test',
          type: 'concept-test' as UseCaseType,
          icon: <TestTube className="w-5 h-5" />,
          description: 'Test product concepts with AI-simulated consumers to predict purchase intent',
        }] : []),
        {
          label: 'Packaging Review',
          type: 'packaging-review-v2',
          icon: <Package className="w-5 h-5" />,
          description: 'Analyze a packaging design with shelf test, competitive comparison, and consumer reactions',
        },
        {
          label: 'Packaging Variant Comparison',
          type: 'packaging-variant-comparison',
          icon: <Package className="w-5 h-5" />,
          description: 'Compare 2-5 packaging variants to find which drives highest purchase intent',
        },
        {
          label: 'Claims Testing',
          type: 'claims-testing',
          icon: <FlaskConical className="w-5 h-5" />,
          description: 'Test and rank product claims using MaxDiff analysis to find what resonates most',
        },
      ],
    },
    {
      title: 'Marketing',
      useCases: [
        {
          label: 'AB Test Messaging',
          type: 'attribution',
          icon: <Target className="w-5 h-5" />,
          description: 'Learn what drives or blocks conversions',
        },
        {
          label: 'A/B Test or Review Creatives and Ads',
          type: 'channel-strategy',
          icon: <TrendingUp className="w-5 h-5" />,
          description: 'Instantly predict which creative will perform best, get insights on what resonates',
        },
        {
          label: 'Creative Rank Test',
          type: 'image-survey',
          icon: <MousePointer className="w-5 h-5" />,
          description: 'Run a ranked click test to identify the best creatives for your listing',
        },
      ],
    },
  ];
};

const DRIVING_ALLOWED_DOMAINS = (import.meta.env.VITE_DRIVING_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const SMARTYPANTS_ALLOWED_DOMAINS = (import.meta.env.VITE_SMARTYPANTS_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const BACCHUS_ALLOWED_DOMAINS = (import.meta.env.VITE_BACCHUS_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const WELLNESSPET_ALLOWED_DOMAINS = (import.meta.env.VITE_WELLNESSPET_ALLOWED_DOMAINS || '')
  .split(',')
  .map((d: string) => d.trim().toLowerCase())
  .filter(Boolean);

const LandingScreen: React.FC<LandingScreenProps> = ({ onSelectUseCase }) => {
  const navigate = useNavigate();
  const { isAdmin, user, isDemoUser, isKnfUser, isBacchusUser, isHbsUser, isAetvUser, isFlavcityUser, isMalkUser, isWellnesspetUser, isMondelezUser, isArcherjerkyUser, isAmysUser, isGaiaUser } = useAuth();
  const { hasFeatureAccess, currentSubscription } = useSubscription();

  const userDomain = user?.email?.split('@')[1]?.toLowerCase() || '';

  // Per-client allowed use-cases now come from the single registry (src/config/clients.ts),
  // replacing the ~13 *_ALLOWED_USE_CASES constants. null = not a registered client (default
  // access via plan/hasFeatureAccess). Admin bypass is applied at each gate, as before.
  const clientUseCases = allowedUseCasesFor(resolveClientKey(user?.email));

  // Check if user is from Panasonic domain (driving-only user)
  const isPanasonicUser = !!userDomain && DRIVING_ALLOWED_DOMAINS.includes(userDomain);

  // Check if user is from Smartypants domain
  const isSmartypantsUser = !!userDomain && SMARTYPANTS_ALLOWED_DOMAINS.includes(userDomain);

  const categories = getCategories({ isAdmin, isPanasonicUser, isSmartypantsUser, isDemoUser, isKnfUser, isBacchusUser, isHbsUser, isWellnesspetUser, isMondelezUser, isArcherjerkyUser, isAmysUser, isGaiaUser });

  const handleUseCaseClick = (useCase: UseCaseOption) => {
    if (useCase.redirect) {
      if (useCase.type === 'healthcare-chat-v2') {
        navigate('/ask-natural-organic');
        return;
      }
      if (useCase.type === 'bluepill-chat') {
        navigate('/chat-with-bluepill');
        return;
      }
      if (useCase.type === 'hbs-chat') {
        window.open('/ask-natural-organic', '_blank');
        return;
      }
      if (useCase.type === 'survey-creator') {
        navigate('/survey-creator');
        return;
      }
      if (useCase.type === 'survey-chat-154') {
        // Landing entry point = the client's standalone survey; the in-chat switcher then
        // lets them change surveys. IDs resolve in src/config/segments.ts (read from the
        // environment) and must match SURVEY_CHAT_CLIENTS in backend survey_chat.py.
        const entrySurveyId = isArcherjerkyUser ? SURVEY_CHAT_ENTRY_IDS.ARCHERJERKY
          : (isAmysUser || user?.email === 'puneet@blue-pill.ai') ? SURVEY_CHAT_ENTRY_IDS.AMYS
          : SURVEY_CHAT_ENTRY_IDS.DEFAULT;
        navigate(`/surveys/${entrySurveyId}/chat?from=landing`);
        return;
      }
    }

    onSelectUseCase(useCase.type, useCase.label);
  };

  // Check if a use case should be disabled for Panasonic users
  const isDisabledForPanasonic = (useCaseType: UseCaseType) => {
    if (!isPanasonicUser) return false;
    // For Panasonic users, only driving-concept-test is enabled
    return useCaseType !== 'driving-concept-test';
  };

  return (
    <div className="max-w-6xl mx-auto animate-fadeIn">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Start a new study.
        </h1>
        <p className="text-gray-500">
          Run research, test ideas, and evaluate marketing with your AI audience.
        </p>
      </div>

      {(() => {
        // Calculate visible categories for dynamic grid sizing
        const visibleCategories = categories.filter((category) => {
          // A registered client (non-admin) sees a category only if it holds at least one
          // use-case that client is allowed. Non-clients and admins see every category.
          if (clientUseCases && !isAdmin) {
            return category.useCases.some((uc) => clientUseCases.includes(uc.type));
          }
          return true;
        });

        // Dynamic grid classes based on visible category count
        const gridClasses = visibleCategories.length === 2
          ? "grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          : visibleCategories.length === 1
            ? "grid grid-cols-1 gap-8 max-w-md mx-auto"
            : "grid grid-cols-1 md:grid-cols-3 gap-8";

        return (
          <div className={gridClasses}>
            {visibleCategories.map((category) => (
          <div key={category.title}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {category.title}
            </h3>
            <div className="flex flex-col gap-4">
              {category.useCases.map((useCase) => {
                // A registered client (non-admin) only sees the use-cases in its registry
                // allow-list; everything else is hidden for that client.
                if (clientUseCases && !isAdmin && !clientUseCases.includes(useCase.type)) return null;

                const isChatRedirect = useCase.redirect;
                // Panasonic users always have access to driving-concept-test
                const isPanasonicDrivingAccess = isPanasonicUser && useCase.type === 'driving-concept-test';
                // A registered client always has access to its allow-listed use-cases (bypasses
                // the subscription/plan check, matching the previous per-client behavior).
                const isClientAccess = !!clientUseCases && clientUseCases.includes(useCase.type);
                // HBS Chat is always accessible for HBS users (no subscription check needed)
                const isHbsChatForHbsUser = useCase.type === 'hbs-chat' && isHbsUser;
                const hasAccess = isAdmin || isChatRedirect || isHbsChatForHbsUser || isPanasonicDrivingAccess || isClientAccess || hasFeatureAccess(useCase.type as string);
                const isLocked = !hasAccess;
                const isPanasonicDisabled = isDisabledForPanasonic(useCase.type);

                // For Panasonic users: gray out all except driving-concept-test
                if (isPanasonicDisabled) {
                  return (
                    <SimulationCard
                      key={useCase.type}
                      icon={useCase.icon}
                      title={useCase.label}
                      description={useCase.description}
                      disabled
                      onClick={() => {}}
                    />
                  );
                }

                if (isLocked) {
                  return (
                    <LockedFeature
                      key={useCase.type}
                      feature={useCase.label}
                      requiredPlan={currentSubscription?.id === 'free' ? 'Starter' : 'Higher tier'}
                    >
                      <SimulationCard
                        icon={useCase.icon}
                        title={useCase.label}
                        description={useCase.description}
                        disabled
                        onClick={() => {}}
                      />
                    </LockedFeature>
                  );
                }

                return (
                  <SimulationCard
                    key={useCase.type}
                    icon={useCase.icon}
                    title={useCase.label}
                    description={useCase.description}
                    onClick={() => handleUseCaseClick(useCase)}
                  />
                );
              })}
            </div>
          </div>
        ))}
          </div>
        );
      })()}
    </div>
  );
};

export default LandingScreen;
